import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef } from '@angular/core';
import { Resource } from '../models/resource';
import { PassDataService } from './pass-data/pass-data.service';

/**
 * Whether the current user may call a given API, read from the resource list the
 * backend already returns for the sidebar: permission is a grant on the resource,
 * never a role name compared in the client.
 * UX only - the backend refuses the call on its own.
 *
 * The grant list arrives asynchronously and changes on domain switch, so it is
 * held in a signal: a consumer reading it in a field initializer would otherwise
 * freeze the empty pre-login set forever.
 */
@Injectable({ providedIn: 'root' })
export class ResourceAccessService {
  private readonly passDataService = inject(PassDataService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly granted: WritableSignal<ReadonlySet<string>> = signal(new Set<string>());

  constructor() {
    this.passDataService.resources$.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((resources: Resource[]) => {
        this.granted.set(new Set((resources ?? []).map(r => this.normalize(r.Url))));
      });
  }

  /** Recomputes whenever the grant list changes; safe in a field initializer. */
  public canAccessSignal(url: string): Signal<boolean> {
    const normalized: string = this.normalize(url);
    return computed(() => this.granted().has(normalized));
  }

  /** Point-in-time check, only for call sites that run after the grants arrived. */
  public canAccess(url: string): boolean {
    return this.granted().has(this.normalize(url));
  }

  // Resource.Url is stored with a leading slash, callers use the bare endpoint.
  private normalize(url: string): string {
    const trimmed: string = (url ?? '').trim().toLowerCase();
    return trimmed.startsWith('/') ? trimmed.slice(1) : trimmed;
  }
}
